import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { C, METRIC_LABELS } from '../../utils/plantConstants';
import { status } from '../../utils/plantModel';

// Day-of-year (1-365) for a timestamp, clamped to the model's 365-day year.
function dayOfYear(ms) {
	const d = new Date(ms);
	const start = new Date(d.getFullYear(), 0, 1);
	const doy = Math.floor((d - start) / 86400000) + 1;
	return Math.min(365, Math.max(1, doy));
}

function fmtVal(metric, v) {
	if (metric === 'light') return `${Math.round(v).toLocaleString()} lux·h`;
	if (metric === 'nutrition') return `${Math.round(v).toLocaleString()} µS/cm`;
	if (metric === 'temperature') return `${v.toFixed(1)}°C`;
	return `${v.toFixed(0)}%`;
}

function fmtAxis(metric, v) {
	if (metric === 'light' || metric === 'nutrition') return v >= 1000 ? `${Math.round(v / 100) / 10}k` : `${Math.round(v)}`;
	return `${Math.round(v)}`;
}

const STATUS = C.status_colors; // optimal (green) / tolerated (orange) / out (red)
const DAY = 86400000;

// The reference band is drawn in neutral grey rather than the metric's own
// colour. The device lines carry a validated categorical palette, and a yellow
// line over a yellow light band (or a red one over the temperature band) would
// be unreadable — so here the band recedes to background and colour means
// "which device".
const BAND_COLOR = '#5b6470';

// Two bands are the same when every day matches. Cheap enough at 365 entries,
// and it runs once per preview response.
function sameBands(a, b) {
	if (a === b) return true;
	if (!a || !b || a.length !== b.length) return false;
	return a.every((x, i) => {
		const y = b[i];
		return x.target_min === y.target_min && x.optimal_min === y.optimal_min && x.optimal_max === y.optimal_max && x.target_max === y.target_max;
	});
}

// MultiDeviceChart plots several devices' measurements over time against the
// single profile band they are all graded on — one line per device, the band
// drawn once behind them.
//
// devices:   [{ id, label, color, points }] where points is [[timestampMs, value]].
// bands:     the 365-entry day-of-year band array all the devices share, or null.
// rangeDays: how many days back from today the x-axis spans, or null for the
//            whole calendar year. The axis always covers the full requested
//            window, so "7 days" reads as seven days even when a device reported
//            on only two of them — the gaps are the point.
const MultiDeviceChart = ({ metric, bands, devices, rangeDays = null }) => {
	const style = C.plot_style[metric];
	const hasBands = Array.isArray(bands) && bands.length > 0;
	const fullYear = rangeDays === null;

	const option = useMemo(() => {
		const withPoints = devices.filter((d) => (d.points || []).length > 0);
		// A band alone is still worth drawing; nothing at all is not.
		if (withPoints.length === 0 && !hasBands) return null;

		// The backend returns one band per day-of-year (index 0 == doy 1).
		const bandAt = (doy) => bands[Math.min(bands.length - 1, Math.max(0, doy - 1))];

		// Every device's readings land on the same daily grid so the axis pointer
		// lines them up; a day a device did not report is null, and connectNulls
		// stays off so the gap is visible rather than bridged.
		let gridStart;
		let gridEnd;
		if (fullYear) {
			const latest = Math.max(...withPoints.map((d) => d.points[d.points.length - 1][0]), 0) || Date.now();
			const year = new Date(latest).getFullYear();
			gridStart = Math.floor(Date.UTC(year, 0, 1) / DAY);
			gridEnd = Math.floor(Date.UTC(year, 11, 31) / DAY);
		} else {
			// Anchored to today rather than to the newest reading, so the window
			// matches the one the health check samples and a device that stopped
			// reporting shows the silence instead of hiding it by rescaling.
			gridEnd = Math.floor(Date.now() / DAY);
			gridStart = gridEnd - (rangeDays - 1);
		}

		const tolBase = [];
		const tolBand = [];
		const optBase = [];
		const optBand = [];
		if (hasBands) {
			for (let d = gridStart; d <= gridEnd; d++) {
				const t = d * DAY;
				const b = bandAt(dayOfYear(t));
				tolBase.push([t, b.target_min]);
				tolBand.push([t, b.target_max - b.target_min]);
				optBase.push([t, b.optimal_min]);
				optBand.push([t, b.optimal_max - b.optimal_min]);
			}
		}

		// On a short range every reading gets a marker. That is not decoration:
		// connectNulls is off, so a day flanked by two non-reporting days draws no
		// line segment at all and would be invisible without its own symbol.
		// Across a full year 365 markers per device would be mush, so they are
		// dropped there — the trade-off the year view accepts.
		const dense = gridEnd - gridStart > 60;

		const deviceSeries = devices.map((dev) => {
			const byDay = new Map();
			for (const p of dev.points || []) byDay.set(Math.floor(p[0] / DAY), p[1]);
			const trace = [];
			for (let d = gridStart; d <= gridEnd; d++) {
				trace.push([d * DAY, byDay.has(d) ? byDay.get(d) : null]);
			}
			return {
				name: dev.label,
				type: 'line',
				data: trace,
				symbol: 'circle',
				symbolSize: 8,
				showSymbol: !dense,
				connectNulls: false,
				smooth: false,
				lineStyle: { width: 2, color: dev.color },
				itemStyle: { color: dev.color },
				emphasis: { focus: 'series' },
				z: 5,
			};
		});

		const invisibleArea = { color: 'transparent' };
		const bandSeries = hasBands
			? [
					{ name: 'tol_base', type: 'line', stack: 'tol', data: tolBase, symbol: 'none', lineStyle: { width: 0 }, areaStyle: invisibleArea, silent: true, z: 1 },
					{ name: 'Tolerated', type: 'line', stack: 'tol', data: tolBand, symbol: 'none', lineStyle: { width: 0 }, areaStyle: { color: BAND_COLOR, opacity: 0.1 }, silent: true, z: 1 },
					{ name: 'opt_base', type: 'line', stack: 'opt', data: optBase, symbol: 'none', lineStyle: { width: 0 }, areaStyle: invisibleArea, silent: true, z: 2 },
					{ name: 'Optimal', type: 'line', stack: 'opt', data: optBand, symbol: 'none', lineStyle: { width: 0 }, areaStyle: { color: BAND_COLOR, opacity: 0.18 }, silent: true, z: 2 },
			  ]
			: [];

		return {
			grid: { left: 56, right: 16, top: 40, bottom: 56 },
			animation: false,
			legend: {
				top: 6,
				type: 'scroll',
				data: devices.map((d) => d.label),
				textStyle: { color: '#5b6470', fontSize: 11 },
				itemWidth: 14,
				itemHeight: 8,
			},
			xAxis: {
				type: 'time',
				axisTick: { show: false },
				axisLine: { lineStyle: { color: '#e2e6ea' } },
				axisLabel: { color: '#aeb4ba', fontFamily: 'ui-monospace, monospace', fontSize: 10 },
				splitLine: { show: true, lineStyle: { color: BAND_COLOR, opacity: 0.12, type: [2, 3] } },
			},
			yAxis: {
				type: 'value',
				scale: true,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { color: '#aeb4ba', fontFamily: 'ui-monospace, monospace', fontSize: 10, formatter: (v) => fmtAxis(metric, v) },
				splitLine: { lineStyle: { color: BAND_COLOR, opacity: 0.12, type: [2, 3] } },
			},
			dataZoom: [
				{ type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
				{ type: 'slider', xAxisIndex: 0, start: 0, end: 100, bottom: 12, height: 18 },
			],
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'line', lineStyle: { type: 'dashed', color: '#9aa0a6' } },
				backgroundColor: 'rgba(43,48,54,0.96)',
				borderWidth: 0,
				textStyle: { color: '#fff', fontSize: 11 },
				// Every device that reported on the hovered day is listed by name
				// and id, each coloured to match its line and tagged with how the
				// health check reads it against the band.
				formatter: (ps) => {
					const rows = ps.filter((p) => p.value && p.value[1] != null && !p.seriesName.startsWith('tol_') && !p.seriesName.startsWith('opt_'));
					const t = rows.length ? rows[0].value[0] : ps[0]?.axisValue;
					if (t == null) return '';
					// Always date-only: this chart plots daily buckets, so the
					// 00:00 a full timestamp would add is noise.
					const date = new Date(t).toLocaleDateString();
					const b = hasBands ? bandAt(dayOfYear(t)) : null;

					const head = `<div style="font-family:ui-monospace,monospace">${date}`;
					const range = b ? `<br/><span style="opacity:.7">optimal ${fmtVal(metric, b.optimal_min)} – ${fmtVal(metric, b.optimal_max)}</span>` : '';
					if (rows.length === 0) return `${head}${range}<br/>no readings</div>`;

					const lines = rows
						.map((p) => {
							const v = p.value[1];
							let tag = '';
							if (b) {
								const s = status(b, v);
								const label = s === 'optimal' ? 'optimal' : s === 'tolerated' ? 'warning' : 'error';
								const color = s === 'optimal' ? STATUS.optimal : s === 'tolerated' ? STATUS.tolerated : STATUS.out;
								tag = ` <span style="color:${color}">(${label})</span>`;
							}
							return `<br/>${p.marker}${p.seriesName} — ${fmtVal(metric, v)}${tag}`;
						})
						.join('');
					return `${head}${range}${lines}</div>`;
				},
			},
			series: [...bandSeries, ...deviceSeries],
		};
	}, [metric, bands, devices, hasBands, fullYear, rangeDays]);

	return (
		<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
			<div className="flex items-center gap-2 px-4 pt-3 pb-1 flex-wrap">
				<span className="w-2.5 h-2.5 rounded" style={{ background: style.opt }} />
				<span className="font-semibold text-text-strong">{METRIC_LABELS[metric]}</span>
				<span className="text-xs text-text-weak font-mono ml-1">{style.unit}</span>
				{hasBands && (
					<div className="ml-auto flex items-center gap-3 text-[10px] text-text-weak font-mono">
						<span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: BAND_COLOR, opacity: 0.2 }} />tolerated</span>
						<span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm inline-block" style={{ background: BAND_COLOR, opacity: 0.35 }} />optimal</span>
					</div>
				)}
			</div>
			{option ? (
				<ReactECharts option={option} style={{ height: 320, width: '100%' }} notMerge={true} />
			) : (
				<div className="h-[320px] flex items-center justify-center text-sm text-text-weak">No measurements yet.</div>
			)}
		</div>
	);
};

export { sameBands };
export default MultiDeviceChart;
